import type { AuditEventList, CurrentSession } from '@elite-residence/api-client';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useApiClient } from '../api/ApiClientProvider';
import { type Dictionary, useText } from '../i18n/LocaleProvider';
import { Alert, Field, Loading } from '../ui/ui';

const text: Dictionary<
  | 'title'
  | 'branch'
  | 'actor'
  | 'action'
  | 'targetKind'
  | 'targetId'
  | 'from'
  | 'to'
  | 'search'
  | 'empty'
  | 'failed'
  | 'more'
  | 'recorded'
  | 'reason'
  | 'changes'
  | 'before'
  | 'after'
> = {
  en: {
    title: 'Audit',
    more: 'Load more',
    recorded: 'Recorded at (UTC)',
    reason: 'Reason',
    changes: 'Changes',
    before: 'Before',
    after: 'After',
    branch: 'Branch',
    actor: 'Actor',
    action: 'Action',
    targetKind: 'Target kind',
    targetId: 'Target',
    from: 'From',
    to: 'To',
    search: 'Search',
    empty: 'No audit events.',
    failed: 'The audit events could not be loaded.',
  },
  ar: {
    title: 'السجل',
    more: 'تحميل المزيد',
    recorded: 'وقت التسجيل (UTC)',
    reason: 'السبب',
    changes: 'التغييرات',
    before: 'قبل',
    after: 'بعد',
    branch: 'الفرع',
    actor: 'المنفذ',
    action: 'الإجراء',
    targetKind: 'نوع الهدف',
    targetId: 'الهدف',
    from: 'من',
    to: 'إلى',
    search: 'بحث',
    empty: 'لا توجد أحداث.',
    failed: 'تعذر تحميل أحداث السجل.',
  },
};

export function AuditPage({ session }: { readonly session: CurrentSession }) {
  const client = useApiClient();
  const t = useText(text);
  const [form, setForm] = useState({
    branchId: '',
    actorStaffId: '',
    action: '',
    targetKind: '',
    targetId: '',
    from: '',
    to: '',
  });
  const [query, setQuery] = useState(form);
  const events = useInfiniteQuery<AuditEventList, Error, InfiniteData<AuditEventList>, readonly unknown[], string>({
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    queryKey: ['audit', query],
    enabled: session.permissions.includes('audit.view'),
    queryFn: async ({ signal, pageParam }) => {
      const response = await client.searchAudit(
        {
          query: {
            limit: '100',
            ...(pageParam ? { cursor: pageParam } : {}),
            ...(query.branchId ? { branchId: query.branchId } : {}),
            ...(query.actorStaffId ? { actorStaffId: query.actorStaffId } : {}),
            ...(query.action ? { action: query.action } : {}),
            ...(query.targetKind ? { targetKind: query.targetKind } : {}),
            ...(query.targetId ? { targetId: query.targetId } : {}),
            ...(query.from ? { from: query.from } : {}),
            ...(query.to ? { to: query.to } : {}),
          },
        },
        { signal },
      );
      if (response.status !== 200) throw new Error('audit_unavailable');
      if (pageParam && response.body.nextCursor === pageParam) throw new Error('audit_cursor_repeated');
      return response.body;
    },
  });
  const rows = events.data?.pages.flatMap((page) => page.events) ?? [];
  const set = (field: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm({ ...form, [field]: event.target.value });
  return (
    <main>
      <h1>{t.title}</h1>
      <form
        className="er-card"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(form);
        }}
        noValidate
      >
        <Field id="au-branch" label={t.branch}>
          <input id="au-branch" dir="ltr" value={form.branchId} onChange={set('branchId')} />
        </Field>
        <Field id="au-actor" label={t.actor}>
          <input id="au-actor" dir="ltr" value={form.actorStaffId} onChange={set('actorStaffId')} />
        </Field>
        <Field id="au-action" label={t.action}>
          <input id="au-action" dir="ltr" value={form.action} onChange={set('action')} />
        </Field>
        <Field id="au-kind" label={t.targetKind}>
          <input id="au-kind" dir="ltr" value={form.targetKind} onChange={set('targetKind')} />
        </Field>
        <Field id="au-target" label={t.targetId}>
          <input id="au-target" dir="ltr" value={form.targetId} onChange={set('targetId')} />
        </Field>
        <Field id="au-from" label={t.from}>
          <input id="au-from" type="date" value={form.from} onChange={set('from')} />
        </Field>
        <Field id="au-to" label={t.to}>
          <input id="au-to" type="date" value={form.to} onChange={set('to')} />
        </Field>
        <button className="er-button" type="submit">
          {t.search}
        </button>
      </form>
      {events.isPending ? (
        <Loading />
      ) : events.isError ? (
        <Alert kind="error">{t.failed}</Alert>
      ) : rows.length === 0 ? (
        <p>{t.empty}</p>
      ) : (
        <table className="er-table">
          <thead>
            <tr>
              <th>{t.recorded}</th>
              <th>{t.branch}</th>
              <th>{t.action}</th>
              <th>{t.targetKind}</th>
              <th>{t.targetId}</th>
              <th>{t.actor}</th>
              <th>{t.reason}</th>
              <th>{t.changes}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((event) => (
              <tr key={event.id}>
                <td>
                  <time dir="ltr" dateTime={event.recordedAt}>
                    {event.recordedAt}
                  </time>
                </td>
                <td className="er-mono">{event.branchId ?? '—'}</td>
                <td>{event.action}</td>
                <td>{event.targetKind}</td>
                <td className="er-mono">{event.targetId}</td>
                <td className="er-mono">{event.actorStaffId ?? event.systemActor ?? event.actorKind}</td>
                <td>{event.reason ?? '—'}</td>
                <td>
                  {event.beforeRedacted !== null || event.afterRedacted !== null ? (
                    <details>
                      <summary>{t.changes}</summary>
                      {event.beforeRedacted !== null && (
                        <>
                          <strong>{t.before}</strong>
                          <pre dir="auto">{event.beforeRedacted}</pre>
                        </>
                      )}
                      {event.afterRedacted !== null && (
                        <>
                          <strong>{t.after}</strong>
                          <pre dir="auto">{event.afterRedacted}</pre>
                        </>
                      )}
                    </details>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {events.hasNextPage && (
        <button
          className="er-button secondary"
          type="button"
          disabled={events.isFetchingNextPage}
          onClick={() => {
            void events.fetchNextPage();
          }}
        >
          {t.more}
        </button>
      )}
    </main>
  );
}
